import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AppShell } from './AppShell';
import { ProtectedRoute } from './ProtectedRoute';
import { AdminShell } from './admin/AdminShell';
import { AdminEventProvider } from './admin/AdminEventContext';
import { ExhibitorDetailPage } from '@/features/admin/exhibitors/pages/ExhibitorDetailPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ScannerPage } from '@/features/scanner/pages/ScannerPage';
import { LeadsPage } from '@/features/leads/pages/LeadsPage';
import { LeadDetailPage } from '@/features/leads/pages/LeadDetailPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { EventsListPage } from '@/features/admin/events/pages/EventsListPage';
import { EventEditPage } from '@/features/admin/events/pages/EventEditPage';
import { ExhibitorsListPage } from '@/features/admin/exhibitors/pages/ExhibitorsListPage';
import { SettingsPage } from '@/features/settings/pages/SettingsPage';
import { DelegatesPage } from '@/features/admin/delegates/pages/DelegatesPage';
import { UsersPage } from '@/features/admin/users/pages/UsersPage';
import { AnalyticsPage } from '@/features/admin/analytics/pages/AnalyticsPage';
import { TeamPage } from '@/features/team/pages/TeamPage';

/** Wraps an admin page: role guard + AdminEventProvider (shared event scope) + AdminShell (sidebar/topbar), so each route below stays a one-liner. */
function AdminRoute({
  title,
  allowedRoles,
  children
}: {
  title: string;
  allowedRoles: ('SystemAdmin' | 'EventOrganiser' | 'ExhibitorAdmin')[];
  children: ReactNode;
}) {
  return (
    <ProtectedRoute allowedRoles={allowedRoles}>
      <AdminEventProvider>
        <AdminShell title={title}>{children}</AdminShell>
      </AdminEventProvider>
    </ProtectedRoute>
  );
}

const EVENT_MANAGER_ROLES = ['SystemAdmin', 'EventOrganiser'] as const;

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* ---- Exhibitor-facing flow (offline-critical core) ---- */}
      <Route
        path="/scan"
        element={
          <ProtectedRoute allowedRoles={['ExhibitorAdmin', 'ExhibitorStaff']}>
            <AppShell>
              <ScannerPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/leads"
        element={
          <ProtectedRoute allowedRoles={['ExhibitorAdmin', 'ExhibitorStaff']}>
            <AppShell>
              <LeadsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/leads/:scanId"
        element={
          <ProtectedRoute allowedRoles={['ExhibitorAdmin', 'ExhibitorStaff']}>
            <AppShell>
              <LeadDetailPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppShell>
              <DashboardPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/team"
        element={
          <ProtectedRoute allowedRoles={['ExhibitorAdmin']}>
            <AppShell>
              <TeamPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      
      {/* ---- Admin portal (desktop-oriented; Stage 11) ---- */}
      <Route
        path="/admin/events"
        element={
          <AdminRoute title="Events" allowedRoles={[...EVENT_MANAGER_ROLES]}>
            <EventsListPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/events/:eventId"
        element={
          <AdminRoute title="Edit Event" allowedRoles={[...EVENT_MANAGER_ROLES]}>
            <EventEditPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/exhibitors"
        element={
          <AdminRoute title="Exhibitors" allowedRoles={[...EVENT_MANAGER_ROLES]}>
            <ExhibitorsListPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/exhibitors/:exhibitorId"
        element={
          <AdminRoute title="Exhibitor" allowedRoles={[...EVENT_MANAGER_ROLES]}>
            <ExhibitorDetailPage />
          </AdminRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['ExhibitorAdmin']}>
            <AppShell>
              <SettingsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/delegates"
        element={
          <AdminRoute title="Delegates" allowedRoles={[...EVENT_MANAGER_ROLES]}>
            <DelegatesPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute title="Users" allowedRoles={[...EVENT_MANAGER_ROLES, 'ExhibitorAdmin']}>
            <UsersPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <AdminRoute title="Analytics" allowedRoles={[...EVENT_MANAGER_ROLES]}>
            <AnalyticsPage />
          </AdminRoute>
        }
      />

      <Route path="/" element={<Navigate to="/scan" replace />} />
      <Route path="*" element={<Navigate to="/scan" replace />} />
    </Routes>
  );
}
